import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Loader2, AlertTriangle, ChevronDown, ChevronRight, ExternalLink,
  Navigation, Shield, Globe,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  serpAnalysisApi,
  type NavAnalysisSite,
  type NavCommonItem,
  type NavTreeNode,
} from "@/api/serp-analysis";

// ── Nav source badge color ──

const SOURCE_COLORS: Record<string, string> = {
  quickscraper: "bg-blue-100 text-blue-800",
  browseract: "bg-purple-100 text-purple-800",
  both: "bg-green-100 text-green-800",
  other: "bg-gray-100 text-gray-800",
};

// ── Depth color for nav tree levels ──

const DEPTH_COLORS = [
  "bg-blue-100 text-blue-800",
  "bg-green-100 text-green-800",
  "bg-orange-100 text-orange-800",
  "bg-purple-100 text-purple-800",
];

const DEPTH_LABELS = ["Category", "Subcategory", "Sub-sub", "L4"];

// ── Common nav bar chart ──

function CommonNavChart({ items }: { items: NavCommonItem[] }) {
  const chartData = items.slice(0, 20).map((item) => ({
    label: item.label.length > 25 ? item.label.slice(0, 22) + "..." : item.label,
    count: item.count,
    percentage: item.percentage,
  }));

  if (chartData.length === 0) return null;

  return (
    <div className="h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ left: 140 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 12 }} />
          <Tooltip
            formatter={(value: number, name: string) =>
              name === "count" ? [`${value} sites`, "出現次數"] : [value, name]
            }
          />
          <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Common nav tier section ──

function CommonNavTier({
  title,
  icon,
  color,
  items,
  sitesWithNav,
}: {
  title: string;
  icon: React.ReactNode;
  color: string;
  items: NavCommonItem[];
  sitesWithNav: number;
}) {
  if (items.length === 0) return null;

  return (
    <div>
      <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-3 text-sm">
            <div className="flex-1 min-w-0 truncate">{item.label}</div>
            <div className="w-28 shrink-0">
              <div className="flex items-center gap-1">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${color} rounded-full`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
                <span className="text-xs font-mono w-10 text-right">
                  {item.percentage}%
                </span>
              </div>
            </div>
            <span className="text-xs text-muted-foreground shrink-0">
              {item.count}/{sitesWithNav}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Nav tree renderer (recursive) ──

function NavTreeView({ nodes, depth = 0 }: { nodes: NavTreeNode[]; depth?: number }) {
  return (
    <div className="space-y-0.5">
      {nodes.map((node, i) => (
        <div key={`${node.label}-${i}`}>
          <div
            className="flex items-center gap-2 py-0.5 text-sm"
            style={{ marginLeft: depth * 16 }}
          >
            <Badge
              className={`${DEPTH_COLORS[depth] || DEPTH_COLORS[3]} text-[10px] px-1.5 py-0 shrink-0`}
            >
              {DEPTH_LABELS[depth] || `L${depth + 1}`}
            </Badge>
            <span className="truncate">{node.label}</span>
          </div>
          {node.children && node.children.length > 0 && (
            <NavTreeView nodes={node.children} depth={depth + 1} />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Site card (collapsible) ──

function SiteCard({
  site,
  commonLabels,
}: {
  site: NavAnalysisSite;
  commonLabels: Set<string>;
}) {
  const [open, setOpen] = useState(false);

  // Calculate overlap with common nav
  const overlap = useMemo(() => {
    let matched = 0;
    function check(nodes: NavTreeNode[]) {
      for (const n of nodes) {
        const norm = n.label?.toLowerCase().replace(/[^\w\s\u4e00-\u9fff]/g, "").replace(/\s+/g, " ").trim();
        if (norm && commonLabels.has(norm)) matched++;
        if (n.children) check(n.children);
      }
    }
    check(site.nav_tree);
    return matched;
  }, [site.nav_tree, commonLabels]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 py-3 px-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary font-bold text-lg shrink-0">
                #{site.rank}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{site.domain}</div>
                <div className="text-xs text-muted-foreground">
                  {site.nav_count} items · source: {site.nav_source}
                </div>
              </div>
              {site.waf_blocked && (
                <Badge variant="destructive" className="text-[10px] shrink-0">
                  <Shield className="h-3 w-3 mr-1" />
                  WAF Blocked
                </Badge>
              )}
              {!site.waf_blocked && site.nav_count > 0 && (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  overlap: {overlap}/{commonLabels.size}
                </Badge>
              )}
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              {open ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-4">
            {site.nav_tree.length > 0 ? (
              <NavTreeView nodes={site.nav_tree} />
            ) : (
              <p className="text-sm text-muted-foreground py-2">
                {site.waf_blocked ? "WAF blocked — no navigation data" : "No navigation data available"}
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// ── Main NavAnalysisTab ──

export default function NavAnalysisTab({ requestId }: { requestId: string }) {
  const [view, setView] = useState<"common" | "sites">("common");

  const { data, isLoading, error } = useQuery({
    queryKey: ["analysis-nav", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getNavAnalysis(requestId);
      return res.data;
    },
  });

  // Tier classification
  const tiers = useMemo(() => {
    if (!data?.common_nav) return { essential: [], common: [], niche: [] };
    return {
      essential: data.common_nav.filter((n) => n.percentage >= 50),
      common: data.common_nav.filter((n) => n.percentage >= 25 && n.percentage < 50),
      niche: data.common_nav.filter((n) => n.percentage < 25),
    };
  }, [data]);

  // Common label set for overlap calculation
  const commonLabels = useMemo(() => {
    const set = new Set<string>();
    if (!data?.common_nav) return set;
    for (const item of data.common_nav) {
      const norm = item.label.toLowerCase().replace(/[^\w\s\u4e00-\u9fff]/g, "").replace(/\s+/g, " ").trim();
      set.add(norm);
    }
    return set;
  }, [data]);

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground mt-2">Loading navigation data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center text-red-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>{(error as Error).message}</p>
      </div>
    );
  }

  if (!data?.sites?.length) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <Navigation className="h-8 w-8 mx-auto mb-2" />
        <p>尚無導覽列資料</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <Badge variant="outline">
          <Globe className="h-3 w-3 mr-1" />
          {data.total_sites} 個網站
        </Badge>
        <Badge variant="outline">
          <Navigation className="h-3 w-3 mr-1" />
          {data.sites_with_nav} 有導覽列
        </Badge>
        {data.waf_blocked > 0 && (
          <Badge variant="destructive">
            <Shield className="h-3 w-3 mr-1" />
            {data.waf_blocked} WAF 被擋
          </Badge>
        )}
        <Badge variant="outline">
          {data.common_nav.length} 個共同導覽項
        </Badge>

        <div className="flex-1" />

        <div className="flex gap-1">
          <Button
            size="sm"
            variant={view === "common" ? "default" : "outline"}
            onClick={() => setView("common")}
          >
            共同導覽 Common
          </Button>
          <Button
            size="sm"
            variant={view === "sites" ? "default" : "outline"}
            onClick={() => setView("sites")}
          >
            各站詳情 By Site
          </Button>
        </div>
      </div>

      {/* Common nav view */}
      {view === "common" && (
        <div className="space-y-6">
          {/* Bar chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                跨站共同導覽項 — Top 20
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                百分比 = 此導覽項出現在多少有導覽列的網站中
              </p>
            </CardHeader>
            <CardContent>
              <CommonNavChart items={data.common_nav} />
            </CardContent>
          </Card>

          {/* Tier breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">分層分析</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <CommonNavTier
                title={`必備 Essential (≥50%) — ${tiers.essential.length} 項`}
                icon={<span className="w-3 h-3 rounded-full bg-red-500 inline-block" />}
                color="bg-red-500"
                items={tiers.essential}
                sitesWithNav={data.sites_with_nav}
              />
              <CommonNavTier
                title={`常見 Common (25-50%) — ${tiers.common.length} 項`}
                icon={<span className="w-3 h-3 rounded-full bg-orange-500 inline-block" />}
                color="bg-orange-500"
                items={tiers.common}
                sitesWithNav={data.sites_with_nav}
              />
              <CommonNavTier
                title={`差異化 Niche (<25%) — ${tiers.niche.length} 項`}
                icon={<span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />}
                color="bg-blue-500"
                items={tiers.niche.slice(0, 30)}
                sitesWithNav={data.sites_with_nav}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Per-site view */}
      {view === "sites" && (
        <div className="space-y-2">
          {data.sites.map((site) => (
            <SiteCard key={site.rank} site={site} commonLabels={commonLabels} />
          ))}
        </div>
      )}
    </div>
  );
}
